import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  collection, onSnapshot, doc, getDoc, setDoc, updateDoc,
  deleteDoc, serverTimestamp, query, orderBy,
} from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/firebaseConfig';
import './MembersPage.css';

const ALL = 'All';
const STATUSES = ['awaiting_payment', 'awaiting_verification', 'approved', 'rejected'];

// Public signup pages are unauthenticated, so they can only record the Storage
// PATH of an upload — minting a download URL is a `read`, which those pages are
// not allowed to perform. Admins are authenticated, so the review UI resolves
// the path here. `url` is the legacy field on applications submitted before the
// switch; prefer it when present so old rows keep rendering.
function resolveUpload(url, path) {
  if (url) return Promise.resolve(url);
  if (!path) return Promise.resolve('');
  return getDownloadURL(ref(storage, path)).catch(() => '');
}

function useUploadUrl(url, path) {
  const [src, setSrc] = useState(url || '');
  useEffect(() => {
    let alive = true;
    resolveUpload(url, path).then((u) => { if (alive) setSrc(u); });
    return () => { alive = false; };
  }, [url, path]);
  return src;
}

const _months = ['Jan','Feb','Mar','Apr','May','Jun',
                 'Jul','Aug','Sep','Oct','Nov','Dec'];

function fmtWhen(ts) {
  if (!ts?.toDate) return '—';
  const d = ts.toDate();
  return `${d.getDate()} ${_months[d.getMonth()]} ${d.getFullYear()}`;
}

const STATUS_COLOR = {
  awaiting_payment:       '#9A9A9A',
  awaiting_verification:  '#D4B574',
  approved:               '#22C55E',
  rejected:               '#E63946',
};
const STATUS_LABEL = {
  awaiting_payment:       'Awaiting Payment',
  awaiting_verification:  'Awaiting Verification',
  approved:               'Approved',
  rejected:               'Rejected',
};

function generateMemberId() {
  const year   = new Date().getFullYear();
  const suffix = Math.floor(Math.random() * 900) + 100;
  return `TF-${year}-${suffix}`;
}

function oneYearFromToday() {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
}

// Renewals & upgrades extend FROM the current expiry if it's still in the
// future (don't lose time the member already paid for); otherwise extend
// from today. Matches the logic of the in-panel "Renew" button on
// MembersPage so both renewal paths behave identically.
function extendExpiry(currentExpiry) {
  const todayStr = new Date().toISOString().slice(0, 10);
  const base = currentExpiry && currentExpiry > todayStr
    ? currentExpiry
    : todayStr;
  const d = new Date(base);
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
}

const TIER_LABEL = {
  basic:    'Basic',
  standard: 'Standard',
  apponly:  'Team Fronx Mobile Access',
  premium:  'Premium',
};
const TIER_COLOR = {
  basic:    '#9A9A9A',
  standard: '#D4B574',
  apponly:  '#3B82F6',
  premium:  '#22C55E',
};

export default function PendingApplicationsPage() {
  const [apps,    setApps]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [status,  setStatus]  = useState('awaiting_verification');
  const [toast,   setToast]   = useState(null);
  const [actioning, setActioning] = useState(null);

  useEffect(() => {
    const q = query(
      collection(db, 'membership_applications'),
      orderBy('submitted_at', 'desc'),
    );
    const unsub = onSnapshot(q, (snap) => {
      setApps(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, []);

  const filtered = useMemo(() => {
    return apps.filter(a => status === ALL || a.status === status);
  }, [apps, status]);

  const counts = useMemo(() => {
    return apps.reduce((acc, a) => {
      const s = a.status || 'awaiting_payment';
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    }, {});
  }, [apps]);

  const approve = useCallback(async (app) => {
    if (!app.phone_number) {
      showToast('Missing phone number — cannot approve.', 'error');
      return;
    }
    const isRenewal = app.type === 'renewal';
    const isUpgrade = app.type === 'upgrade';

    const confirmMsg = isUpgrade
      ? `Upgrade ${app.name} to Premium?\n\nThis flips their tier to ` +
        `premium, enables app access, and sets a 1-year expiry.`
      : isRenewal
        ? `Renew ${app.name}?\n\nThis extends their membership by 1 year ` +
          `and re-enables app access.`
        : `Approve ${app.name}?\n\nThis will create their member account ` +
          `with phone ${app.phone_number}. They can immediately log in.`;

    if (!window.confirm(confirmMsg)) return;

    setActioning(app.id);
    try {
      const phoneKey = app.phone_number;
      const grantsApp = app.grants_app_access === true || app.tier === 'premium';

      // RENEWAL: only update the existing record, don't overwrite all fields.
      if (isRenewal) {
        // Read current expiry so we extend from it (don't strip days the
        // member already paid for).
        const existingSnap = await getDoc(doc(db, 'allowed_users', phoneKey));
        const currentExpiry = existingSnap.exists()
          ? (existingSnap.data()?.membership_expiry || '')
          : '';
        const newExpiry = extendExpiry(currentExpiry);
        await updateDoc(doc(db, 'allowed_users', phoneKey), {
          status:            'active',
          tier:              'premium',
          mobile_app_access: true,
          membership_expiry: newExpiry,
          renewed_at:        serverTimestamp(),
        });
        await updateDoc(doc(db, 'membership_applications', app.id), {
          status:      'approved',
          approved_at: serverTimestamp(),
        });
        showToast(`✅ ${app.name} renewed until ${newExpiry}.`, 'success');
        return;
      }

      // UPGRADE: flip tier on the existing member; unlock app access.
      if (isUpgrade) {
        // Same extend-from-current-expiry logic as renewal - upgrading
        // shouldn't shorten an already-valid membership.
        const existingSnap = await getDoc(doc(db, 'allowed_users', phoneKey));
        const currentExpiry = existingSnap.exists()
          ? (existingSnap.data()?.membership_expiry || '')
          : '';
        const newExpiry = extendExpiry(currentExpiry);
        await updateDoc(doc(db, 'allowed_users', phoneKey), {
          status:            'active',
          tier:              app.upgrading_to || 'premium',
          mobile_app_access: true,
          membership_expiry: newExpiry,
          upgraded_at:       serverTimestamp(),
          upgraded_from:     app.upgrading_from || '',
        });
        await updateDoc(doc(db, 'membership_applications', app.id), {
          status:      'approved',
          approved_at: serverTimestamp(),
        });
        showToast(`✅ ${app.name} upgraded to Premium.`, 'success');
        return;
      }

      // NEW signup -> create full member record. The member doc carries a plain
      // URL because the mobile app renders it directly, so the path recorded by
      // the (unauthenticated) signup page is resolved here, where we have auth.
      const memberId = generateMemberId();
      const memberPhotoUrl = await resolveUpload(
        app.profile_photo_url, app.profile_photo_path,
      );
      await setDoc(doc(db, 'allowed_users', phoneKey), {
        member_id:         memberId,
        name:              app.name,
        phone_number:      phoneKey,
        email:             app.email || '',
        district:          app.district,
        car_variant:       app.car_variant,
        car_color:         app.car_color,
        vehicle_number:    (app.vehicle_number || '').toUpperCase(),
        blood_group:       app.blood_group || '',
        emergency_contact: app.emergency_contact || '',
        engine_variant:    app.engine_variant || '',
        profile_photo_url: memberPhotoUrl,
        tier:              app.tier || 'basic',
        // Only Premium tier opens up the mobile app - others remain
        // active "members" for community/logo purposes only.
        mobile_app_access: grantsApp,
        status:            'active',
        membership_expiry: grantsApp ? oneYearFromToday() : '',
        // Shipping address
        house_no:          app.house_no || '',
        address_line:      app.address_line || '',
        landmark:          app.landmark || '',
        city:              app.city || '',
        state:             app.state || '',
        pincode:           app.pincode || '',
        alternate_phone:   app.alternate_phone || '',
        next_service: {
          due_date: '', odometer_km: 0, service_center: '', notes: '',
        },
        joined_on:  serverTimestamp(),
        invited_by: 'SIGNUP_PUBLIC',
      });

      await updateDoc(doc(db, 'membership_applications', app.id), {
        status:      'approved',
        member_id:   memberId,
        approved_at: serverTimestamp(),
      });

      showToast(`✅ ${app.name} approved as ${memberId}.`, 'success');
    } catch (e) {
      showToast(`Approve failed: ${e.message}`, 'error');
    } finally {
      setActioning(null);
    }
  }, []);

  const reject = useCallback(async (app) => {
    const reason = window.prompt(
      `Reject ${app.name}?\n\nOptional reason (shown internally):`,
      ''
    );
    if (reason === null) return; // cancel

    setActioning(app.id);
    try {
      await updateDoc(doc(db, 'membership_applications', app.id), {
        status:        'rejected',
        reject_reason: reason || '',
        rejected_at:   serverTimestamp(),
      });
      showToast(`${app.name} rejected.`, 'success');
    } catch (e) {
      showToast(`Reject failed: ${e.message}`, 'error');
    } finally {
      setActioning(null);
    }
  }, []);

  const remove = useCallback(async (app) => {
    if (!window.confirm(`Delete application from ${app.name}?`)) return;
    try {
      await deleteDoc(doc(db, 'membership_applications', app.id));
      showToast('Application removed.', 'success');
    } catch (e) {
      showToast(`Delete failed: ${e.message}`, 'error');
    }
  }, []);

  const showToast = (message, type) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Pending Applications</h1>
          <p className="page-subtitle">
            {counts.awaiting_verification ?? 0} awaiting verification ·{' '}
            {counts.approved ?? 0} approved · {counts.rejected ?? 0} rejected
          </p>
        </div>
      </div>

      {toast && (
        <div className={`mem-toast mem-toast--${toast.type}`}>{toast.message}</div>
      )}

      <div className="mem-filters">
        <select className="mem-select"
          value={status}
          onChange={e => setStatus(e.target.value)}
          style={{ minWidth: 240 }}
        >
          <option value={ALL}>All applications</option>
          {STATUSES.map(s => (
            <option key={s} value={s}>{STATUS_LABEL[s]}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="mem-loading"><span className="tf-spinner tf-spinner--red" /></div>
      ) : filtered.length === 0 ? (
        <div className="mem-empty">No applications in this status.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {filtered.map(app => (
            <AppCard
              key={app.id}
              app={app}
              busy={actioning === app.id}
              onApprove={() => approve(app)}
              onReject={() => reject(app)}
              onDelete={() => remove(app)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AppCard({ app, busy, onApprove, onReject, onDelete }) {
  const color = STATUS_COLOR[app.status] || '#9A9A9A';
  const label = STATUS_LABEL[app.status] || app.status;
  const isPending = app.status === 'awaiting_verification';
  const shotUrl  = useUploadUrl(app.payment_screenshot, app.payment_screenshot_path);
  const photoUrl = useUploadUrl(app.profile_photo_url, app.profile_photo_path);

  return (
    <div style={{
      background: '#161618',
      border: '1px solid #1FD4B574',
      borderRadius: 12,
      padding: 16,
      display: 'grid',
      gridTemplateColumns: shotUrl ? '180px 1fr auto' : '1fr auto',
      gap: 18,
      alignItems: 'flex-start',
    }}>
      {/* Screenshot */}
      {shotUrl && (
        <a href={shotUrl} target="_blank" rel="noreferrer">
          <img
            src={shotUrl}
            alt="Payment screenshot"
            style={{
              width: '100%',
              height: 200,
              objectFit: 'cover',
              borderRadius: 8,
              background: '#0F0F0F',
              cursor: 'zoom-in',
              border: '1px solid rgba(212,181,116,0.25)',
            }}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        </a>
      )}

      {/* Form data */}
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
          <span style={{
            padding: '2px 8px',
            background: `${color}22`,
            color, borderRadius: 4,
            fontSize: 10, fontWeight: 700,
            letterSpacing: 1, textTransform: 'uppercase',
          }}>{label}</span>
          {app.type === 'renewal' && (
            <span style={{
              padding: '2px 8px',
              background: 'rgba(59, 130, 246, 0.18)',
              color: '#3B82F6', borderRadius: 4,
              fontSize: 10, fontWeight: 800,
              letterSpacing: 1, textTransform: 'uppercase',
            }}>🔁 Renewal</span>
          )}
          {app.type === 'upgrade' && (
            <span style={{
              padding: '2px 8px',
              background: 'rgba(168, 85, 247, 0.20)',
              color: '#C084FC', borderRadius: 4,
              fontSize: 10, fontWeight: 800,
              letterSpacing: 1, textTransform: 'uppercase',
            }}>
              ⬆ Upgrade
              {app.upgrading_from && (
                <span style={{ marginLeft: 6, opacity: 0.85, fontWeight: 600 }}>
                  ({app.upgrading_from} → {app.upgrading_to || 'premium'})
                </span>
              )}
            </span>
          )}
          {app.tier && (
            <span style={{
              padding: '2px 8px',
              background: `${TIER_COLOR[app.tier] || '#9A9A9A'}22`,
              color:      TIER_COLOR[app.tier] || '#9A9A9A',
              borderRadius: 4,
              fontSize: 10, fontWeight: 800,
              letterSpacing: 1, textTransform: 'uppercase',
            }}>
              {TIER_LABEL[app.tier] || app.tier}
            </span>
          )}
          <span style={{ color: '#B79F6D', fontSize: 11 }}>
            · Submitted {fmtWhen(app.submitted_at)}
          </span>
          {app.amount_inr > 0 && (
            <span style={{ color: '#D4B574', fontSize: 11, fontWeight: 700 }}>
              · ₹{app.amount_inr}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          {photoUrl && (
            <a href={photoUrl} target="_blank" rel="noreferrer">
              <img src={photoUrl} alt={app.name}
                style={{
                  width: 40, height: 40, borderRadius: '50%',
                  objectFit: 'cover',
                  border: '2px solid rgba(212,181,116,0.4)',
                  cursor: 'zoom-in',
                }}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </a>
          )}
          <div style={{ color: '#F7E8BF', fontSize: 16, fontWeight: 700 }}>
            {app.name}
          </div>
        </div>
        <div style={{ color: '#D8C8A4', fontSize: 12, lineHeight: 1.6 }}>
          📱 {app.phone_number}
          {app.email && <> · ✉ {app.email}</>}<br/>
          📍 {app.district} · 🚗 {app.car_variant} · {app.car_color}<br/>
          🎫 {app.vehicle_number}
          {app.blood_group && <> · 🩸 {app.blood_group}</>}
          {app.emergency_contact && <> · 🆘 {app.emergency_contact}</>}
          {app.engine_variant && <><br/>⚙️ {app.engine_variant}</>}
        </div>
        {app.address_line && (
          <div style={{
            marginTop: 6,
            padding: 8,
            background: 'rgba(212,181,116,0.08)',
            borderRadius: 6,
            fontSize: 11,
            color: '#D4B574',
            lineHeight: 1.5,
          }}>
            <strong>Ship to:</strong>{' '}
            {[app.house_no, app.address_line].filter(Boolean).join(', ')}
            {app.landmark && <> (near {app.landmark})</>}
            <br/>
            {[app.city, app.state, app.pincode].filter(Boolean).join(', ')}
            {app.alternate_phone && <><br/>📞 alt: {app.alternate_phone}</>}
          </div>
        )}
        {app.reject_reason && (
          <div style={{
            marginTop: 6, color: '#E63946', fontSize: 11,
            fontStyle: 'italic',
          }}>Rejected: {app.reject_reason}</div>
        )}
        {app.member_id && (
          <div style={{
            marginTop: 6, color: '#22C55E',
            fontSize: 11, fontFamily: 'monospace',
          }}>Member ID: {app.member_id}</div>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {isPending && (
          <>
            <button
              onClick={onApprove}
              disabled={busy}
              style={{
                padding: '8px 14px',
                background: '#22C55E', color: '#000',
                border: 'none', borderRadius: 6,
                fontSize: 12, fontWeight: 800,
                cursor: busy ? 'wait' : 'pointer',
              }}>
              {busy ? '…' : '✓ Approve'}
            </button>
            <button
              onClick={onReject}
              disabled={busy}
              style={{
                padding: '8px 14px',
                background: 'transparent', color: '#E63946',
                border: '1px solid #E63946', borderRadius: 6,
                fontSize: 12, fontWeight: 700,
                cursor: busy ? 'wait' : 'pointer',
              }}>
              ✕ Reject
            </button>
          </>
        )}
        <button onClick={onDelete}
          style={{
            padding: '6px 12px',
            background: 'transparent', color: '#9A9A9A',
            border: '1px solid #2A2A2A', borderRadius: 6,
            fontSize: 11, cursor: 'pointer',
          }}>
          Delete
        </button>
      </div>
    </div>
  );
}
